import React, { useState, useRef, useEffect } from 'react';
import { Upload, Play, Pause, Download, RotateCw, Maximize, Minimize } from 'lucide-react';

export default function ParkingNumberGenerator() {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null); // 裁剪后的图片
  const [bigCanvasCache, setBigCanvasCache] = useState(null); // 缓存的3x3大画布
  const [canvasWidth, setCanvasWidth] = useState(720); // 原始画布宽度，默认720
  const [canvasHeight, setCanvasHeight] = useState(1920); // 原始画布高度，默认1920
  const [viewWidthMultiplier] = useState(2.0); // 可视宽度倍数
  const [number, setNumber] = useState('168');
  const [position, setPosition] = useState({ x: 50, y: 85 });
  const [fontSize, setFontSize] = useState(850);
  const [fontFamily, setFontFamily] = useState('StencilSet');
  const [color, setColor] = useState('#FFD700');
  const [brightness, setBrightness] = useState(50); // 亮度，0-100%，默认50%
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(0);

  // 磨损效果参数
  const [wearIntensity, setWearIntensity] = useState(65);
  const [scratchCount, setScratchCount] = useState(100);

  // 动画参数
  const [isPlaying, setIsPlaying] = useState(true); // 默认true，直接开始播放
  const [motionDirection, setMotionDirection] = useState('forward');
  const [verticalSpeed, setVerticalSpeed] = useState(45);
  const [horizontalSpeed, setHorizontalSpeed] = useState(2);
  const [rotateSpeed, setRotateSpeed] = useState(0.02); // 每秒转数，0.02=50秒一转
  const [rotateMode, setRotateMode] = useState('limited'); // 'limited' = ±90度, 'full' = 360度
  const [rotateDirection, setRotateDirection] = useState(1); // 1 或 -1，旋转方向
  const [autoChangeNumber, setAutoChangeNumber] = useState(false);
  const [changeInterval, setChangeInterval] = useState(5);
  const [rotatePhaseOffset, setRotatePhaseOffset] = useState(0); // 旋转相位偏移
  const [useSimplifiedFormat, setUseSimplifiedFormat] = useState(true); // 临时模式：只生成A171和A-101格式
  const [animationOffset, setAnimationOffset] = useState({ x: 0, y: 0, rotate: 0 });
  const [isInitialized, setIsInitialized] = useState(true); // 默认true，直接开始
  const [animationTime, setAnimationTime] = useState(0);
  const [lastChangeTime, setLastChangeTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 自动模式
  const [autoMode, setAutoMode] = useState(true);
  const [autoCycleDuration, setAutoCycleDuration] = useState(15);
  const [autoRandomOffset, setAutoRandomOffset] = useState(50);
  const [autoCurrentPhase, setAutoCurrentPhase] = useState('forward');
  const [autoPhaseStartTime, setAutoPhaseStartTime] = useState(0);

  // 弧线运动参数
  const [arcStartX, setArcStartX] = useState(0);
  const [arcEndX, setArcEndX] = useState(0);
  const [arcAmplitude, setArcAmplitude] = useState(200);
  const [actualDisplayHeight, setActualDisplayHeight] = useState(0); // 实际显示高度

  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fullscreenCanvasRef = useRef(null); // 全屏专用画布
  const animationRef = useRef(null);
  const previewContainerRef = useRef(null);

  // 字体加载状态
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // 需要预加载的自定义字体列表
  const customFonts = [
    'StencilSet',
    'StencilSans',
    'No Parking',
    'FutstencilSerif',
    'StencilSans fed',
    'TRY Clother',
    'StencilSans Bold',
    'Stencil',
    'stencilla',
    'StencilStd',
  ];

  // 预加载所有字体
  useEffect(() => {
    const preloadFonts = async () => {
      console.log('开始预加载字体...');
      const fontPromises = customFonts.map(async (fontName) => {
        try {
          // 使用 document.fonts API 加载字体
          await document.fonts.load(`100px "${fontName}"`);
          console.log(`字体 "${fontName}" 加载成功`);
          return true;
        } catch (error) {
          console.warn(`字体 "${fontName}" 加载失败:`, error);
          return false;
        }
      });

      await Promise.all(fontPromises);

      // 等待所有字体准备就绪
      await document.fonts.ready;
      console.log('所有字体预加载完成');
      setFontsLoaded(true);
    };

    preloadFonts();
  }, []);

  // 更好的随机数生成器，使用 crypto API
  const secureRandom = () => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] / (0xFFFFFFFF + 1);
  };

  // 随机整数 [0, max)
  const randomInt = (max) => Math.floor(secureRandom() * max);

  // 随机范围整数 [min, max]
  const randomRange = (min, max) => min + Math.floor(secureRandom() * (max - min + 1));

  // 易混淆数字：1 7 6 8 9 0
  const confusingDigits = ['1', '7', '6', '8', '9', '0'];

  // 生成包含易混淆数字的随机数字串
  const generateConfusingNumber = (length) => {
    let result = '';
    for (let i = 0; i < length; i++) {
      result += confusingDigits[randomInt(confusingDigits.length)];
    }
    return result;
  };

  // 临时模式：主要是 A171 和 A-101 两种格式（易混淆数字）
  const simplifiedTemplates = [
    // A171 格式（字母+3位数字，无连字符）- 占70%
    () => `A${generateConfusingNumber(3)}`,
    () => `A${generateConfusingNumber(3)}`,
    () => `A${generateConfusingNumber(3)}`,
    () => `A${generateConfusingNumber(3)}`,
    () => `A${generateConfusingNumber(3)}`,
    () => `A${generateConfusingNumber(3)}`,
    () => `A${generateConfusingNumber(3)}`,
    () => `B${generateConfusingNumber(3)}`,
    () => `C${generateConfusingNumber(3)}`,
    () => `D${generateConfusingNumber(3)}`,

    // A-101 格式（字母-3位数字，有连字符）- 占30%
    () => `A-${generateConfusingNumber(3)}`,
    () => `A-${generateConfusingNumber(3)}`,
    () => `A-${generateConfusingNumber(3)}`,
    () => `B-${generateConfusingNumber(3)}`,
    () => `C-${generateConfusingNumber(3)}`,


    // 纯数字
    () => String(randomRange(1, 99)).padStart(2, '0'),
    () => String(randomRange(1, 999)).padStart(3, '0'),
    () => String(randomRange(1, 9999)).padStart(4, '0'),

    () => `${randomRange(1, 9)}-${randomRange(101, 1099)}`,
  ];

  // 完整模式：所有类型的车位号
  const fullTemplates = [
    // 纯数字
    () => String(randomRange(1, 999)),
    () => String(randomRange(1, 99)).padStart(2, '0'),
    () => String(randomRange(1, 999)).padStart(3, '0'),
    () => String(randomRange(1, 9999)).padStart(4, '0'),

    // 字母+数字
    () => String.fromCharCode(65 + randomInt(26)) + randomRange(1, 99),
    () => String.fromCharCode(65 + randomInt(26)) + String(randomRange(1, 999)).padStart(3, '0'),
    () => String.fromCharCode(65 + randomInt(26)) + String(randomRange(1, 99)).padStart(2, '0'),

    // 数字-数字
    () => `${randomRange(1, 9)}-${String(randomRange(1, 999)).padStart(3, '0')}`,
    () => `${randomRange(1, 99)}-${randomRange(1, 99)}`,
    () => `${randomRange(1, 9)}-${randomRange(101, 1099)}`,
    () => `${randomRange(1, 99)}-${String(randomRange(1, 999)).padStart(3, '0')}`,

    // 字母+数字-字母+数字
    () => {
      const letter1 = String.fromCharCode(65 + randomInt(26));
      const letter2 = String.fromCharCode(65 + randomInt(26));
      const num1 = randomRange(1, 9);
      const num2 = String(randomRange(1, 999)).padStart(3, '0');
      return `${letter1}${num1}-${letter2}${num2}`;
    },
    () => {
      const letter1 = String.fromCharCode(65 + randomInt(26));
      const num1 = randomRange(1, 99);
      return `${letter1}${num1}-${randomRange(1, 999)}`;
    },
    () => {
      const letter = String.fromCharCode(65 + randomInt(26));
      const num1 = randomRange(1, 9);
      const num2 = String(randomRange(1, 999)).padStart(3, '0');
      return `${letter}${num1}-${num2}`;
    },

    // 负号开头
    () => `-${randomRange(1, 9)}`,
    () => `-${randomRange(1, 9)}-${randomRange(101, 1099)}`,
    () => `-${String(randomRange(1, 99)).padStart(2, '0')}`,
    () => `-${String(randomRange(1, 999)).padStart(3, '0')}`,

    // 地下停车场格式
    () => `B${randomRange(1, 5)}-${String(randomRange(1, 999)).padStart(3, '0')}`,
    () => `B${randomRange(1, 3)}-${String.fromCharCode(65 + randomInt(26))}${String(randomRange(1, 99)).padStart(2, '0')}`,
    () => `B${randomRange(1, 5)}-${randomRange(1, 999)}`,

    // 楼层格式
    () => `${randomRange(1, 20)}F-${String(randomRange(1, 99)).padStart(2, '0')}`,
    () => `${randomRange(1, 10)}F-${String.fromCharCode(65 + randomInt(26))}${randomRange(1, 99)}`,
    () => `${randomRange(1, 20)}F-${String(randomRange(1, 999)).padStart(3, '0')}`,

    // 字母-数字格式
    () => {
      const letter = String.fromCharCode(65 + randomInt(26));
      return `${letter}-${String(randomRange(1, 999)).padStart(3, '0')}`;
    },
    () => {
      const letter = String.fromCharCode(65 + randomInt(26));
      return `${letter}-${randomRange(1, 99)}`;
    },

    // 双字母+数字
    () => {
      const letter1 = String.fromCharCode(65 + randomInt(26));
      const letter2 = String.fromCharCode(65 + randomInt(26));
      return `${letter1}${letter2}${randomRange(1, 999)}`;
    },
    () => {
      const letter1 = String.fromCharCode(65 + randomInt(26));
      const letter2 = String.fromCharCode(65 + randomInt(26));
      return `${letter1}${letter2}-${String(randomRange(1, 99)).padStart(2, '0')}`;
    },

    // 分区格式
    () => `${String.fromCharCode(65 + randomInt(26))}区${randomRange(1, 99)}号`,
    () => `${['东', '西', '南', '北'][randomInt(4)]}${randomRange(1, 9)}-${String(randomRange(1, 99)).padStart(2, '0')}`,
    () => `${['东', '西', '南', '北'][randomInt(4)]}区${randomRange(1, 99)}号`,
  ];

  // 根据模式选择模板
  const parkingNumberTemplates = useSimplifiedFormat ? simplifiedTemplates : fullTemplates;

  // 生成随机车位编号
  const generateRandomParkingNumber = () => {
    const template = parkingNumberTemplates[randomInt(parkingNumberTemplates.length)];
    const parkingNumber = template();

    // 随机颜色：黄色或白色
    const colors = ['#FFD700', '#FFFFFF'];
    const randomColor = colors[randomInt(colors.length)];
    setColor(randomColor);

    // 随机亮度：50-85之间
    const randomBrightness = randomRange(50, 85);
    setBrightness(randomBrightness);

    // 随机磨损强度：20-60之间
    const randomWear = randomRange(20, 60);
    setWearIntensity(randomWear);

    // 根据内容选择字体：汉字用黑体，英文数字随机选择（不重复上一次）
    const hasChineseChar = /[一-龥]/.test(parkingNumber);
    if (hasChineseChar) {
      setFontFamily('SimHei'); // 黑体
    } else {
      // 英文数字随机选择各种模板字体（排除当前字体，确保每次都切换）
      // Impact出现概率更高
      const englishFonts = ['StencilSet', 'StencilSans', 'No Parking', 'StencilSans fed', 'TRY Clother', 'StencilSans Bold', 'Stencil', 'stencilla', 'StencilStd', 'Impact', 'Impact', 'Impact', 'Impact'];
      setFontFamily(currentFont => {
        const availableFonts = englishFonts.filter(f => f !== currentFont);
        return availableFonts[randomInt(availableFonts.length)];
      });
    }

    return parkingNumber;
  };

  // 简单的伪随机数生成器（基于种子）
  const seededRandom = (seed) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  // 可用字体列表
  const fonts = [
    'Impact',
    'StencilSet',
    'StencilSans',
    'No Parking',
    'FutstencilSerif',
    'StencilSans fed',
    'TRY Clother',
    'StencilSans Bold',
    'Stencil',
    'stencilla',
    'StencilStd',
    'SimHei',
    'Arial Black',
    'Georgia',
    'Times New Roman',
    'Courier New',
    'Verdana',
    'Trebuchet MS',
    'Comic Sans MS',
    'Brush Script MT',
  ];

  // 处理图片上传 - 直接使用原图
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setBackgroundImage(img);
          setCroppedImage(img);
          setCanvasWidth(img.width);
          setCanvasHeight(img.height);

          // 创建纯黑色画布作为背景
          const blackCanvas = document.createElement('canvas');
          blackCanvas.width = img.width;
          blackCanvas.height = img.height;
          const blackCtx = blackCanvas.getContext('2d');
          blackCtx.fillStyle = '#000000';
          blackCtx.fillRect(0, 0, blackCanvas.width, blackCanvas.height);

          setBigCanvasCache(blackCanvas); // 使用纯黑色画布
          setIsInitialized(true); // 标记已初始化

          // 生成第一个随机车位号
          const firstNumber = generateRandomParkingNumber();
          setNumber(firstNumber);

          // 初始化横向随机位置（左边-1或右边1）
          setArcStartX(secureRandom() > 0.5 ? 1 : -1);
          setArcEndX(secureRandom() > 0.5 ? 1 : -1);
          // 随机旋转起始方向
          setRotatePhaseOffset(secureRandom() > 0.5 ? 0 : Math.PI);

          setIsPlaying(true); // 自动开始播放

          const viewWidth = Math.floor(img.width * 2.0);
          console.log(`图片已加载: ${img.width}x${img.height}`);
          console.log(`使用纯黑色背景`);
          console.log(`可视宽度: ${viewWidth}px (2.0倍)`);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // 绘制带磨损效果的文字
  const drawWornText = (ctx, text, x, y, actualFontSize = fontSize) => {
    ctx.save();

    // 设置文字样式
    ctx.font = `${actualFontSize}px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 根据亮度调整颜色
    const adjustColorBrightness = (hexColor, brightnessPercent) => {
      // 解析hex颜色
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      // 根据亮度调整（brightnessPercent: 0-100）
      const factor = brightnessPercent / 100;
      const newR = Math.round(r * factor);
      const newG = Math.round(g * factor);
      const newB = Math.round(b * factor);
      return `rgb(${newR}, ${newG}, ${newB})`;
    };

    const adjustedColor = adjustColorBrightness(color, brightness);

    // 先绘制描边
    if (strokeWidth > 0) {
      ctx.strokeStyle = adjustColorBrightness(strokeColor, brightness);
      ctx.lineWidth = strokeWidth;
      ctx.strokeText(text, x, y);
    }

    // 绘制填充
    ctx.fillStyle = adjustedColor;
    ctx.fillText(text, x, y);

    // 添加磨损效果
    if (wearIntensity > 0) {
      ctx.globalCompositeOperation = 'destination-out';

      const metrics = ctx.measureText(text);
      const textWidth = metrics.width;
      const textHeight = actualFontSize;

      for (let i = 0; i < scratchCount; i++) {
        const seed = i * 7 + text.length * 13;
        const scratchX = x - textWidth / 2 + seededRandom(seed) * textWidth;
        const scratchY = y - textHeight / 2 + seededRandom(seed + 1) * textHeight;
        const scratchWidth = seededRandom(seed + 2) * (wearIntensity / 3) + 2;
        const scratchHeight = seededRandom(seed + 3) * (wearIntensity / 2) + 5;

        ctx.fillStyle = `rgba(0, 0, 0, ${seededRandom(seed + 4) * 0.5 + 0.3})`;
        ctx.fillRect(scratchX, scratchY, scratchWidth, scratchHeight);
      }

      const dotCount = Math.floor(wearIntensity / 2);
      for (let i = 0; i < dotCount; i++) {
        const seed = i * 11 + text.length * 17 + 1000;
        const dotX = x - textWidth / 2 + seededRandom(seed) * textWidth;
        const dotY = y - textHeight / 2 + seededRandom(seed + 1) * textHeight;
        const dotSize = seededRandom(seed + 2) * (wearIntensity / 10) + 1;

        ctx.beginPath();
        ctx.arc(dotX, dotY, dotSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 0, 0, ${seededRandom(seed + 3) * 0.6 + 0.2})`;
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';
    }

    ctx.restore();
  };

  // 绘制画布内容
  const drawCanvas = React.useCallback((canvas, offsetX = 0, offsetY = 0, rotate = 0) => {
    if (!canvas || !canvasWidth || !canvasHeight) {
      return;
    }

    const ctx = canvas.getContext('2d');

    // 全屏时使用屏幕尺寸，非全屏时使用原图尺寸
    let viewWidth, viewHeight;
    if (isFullscreen) {
      viewWidth = window.innerWidth;
      viewHeight = window.innerHeight;
    } else {
      viewWidth = Math.floor(canvasWidth * viewWidthMultiplier);
      viewHeight = canvasHeight;
    }

    canvas.width = viewWidth;
    canvas.height = viewHeight;

    // 绘制纯黑色背景
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 保存上下文状态
    ctx.save();

    // 计算实际字体大小
    let actualFontSize = fontSize;
    if (isFullscreen) {
      // 先测量文字宽度（用原始字体大小）
      ctx.font = `${fontSize}px ${fontFamily}`;
      const textMetrics = ctx.measureText(number);
      const textWidth = textMetrics.width;
      const textHeight = fontSize;

      // 计算文字旋转时的最大边界（对角线长度）
      const diagonal = Math.sqrt(textWidth * textWidth + textHeight * textHeight);

      // 屏幕宽度留出边距
      const maxWidth = viewWidth * 0.9;

      // 根据对角线计算缩放比例，确保旋转时不超出屏幕
      // 只缩小不放大
      const scale = Math.min(1, maxWidth / diagonal);
      actualFontSize = fontSize * scale;
    }

    // 先移动到文字位置（画布中心 + 偏移），再旋转
    // 这样文字绕自身中心旋转，而不是绕画布中心旋转
    const textX = viewWidth / 2 + offsetX;
    const textY = viewHeight / 2 + offsetY;
    ctx.translate(textX, textY);
    ctx.rotate((rotate * Math.PI) / 180);

    // 绘制文字（在原点，因为已经translate到文字位置了）
    drawWornText(ctx, number, 0, 0, actualFontSize);

    ctx.restore();
  }, [canvasWidth, canvasHeight, viewWidthMultiplier, number, fontSize, fontFamily, color, brightness, strokeColor, strokeWidth, wearIntensity, scratchCount, isFullscreen]);

  // 更新预览 - 加载后立即显示
  useEffect(() => {
    if (canvasWidth && canvasHeight && isInitialized) {
      // 全屏时使用fullscreenCanvasRef，否则使用previewCanvasRef
      const targetCanvas = isFullscreen ? fullscreenCanvasRef.current : previewCanvasRef.current;

      if (targetCanvas) {
        drawCanvas(
          targetCanvas,
          animationOffset.x,
          animationOffset.y,
          animationOffset.rotate
        );
      }

      // 更新实际显示高度
      if (!isFullscreen) {
        setTimeout(() => {
          if (previewCanvasRef.current) {
            const rect = previewCanvasRef.current.getBoundingClientRect();
            setActualDisplayHeight(rect.height);
          }
        }, 100);
      }
    }
  }, [
    drawCanvas,
    canvasWidth,
    canvasHeight,
    animationOffset,
    isInitialized,
    isFullscreen,
  ]);

  // 动画循环
  useEffect(() => {
    if (isPlaying && canvasWidth && canvasHeight) {
      let time = animationTime;
      let lastChange = lastChangeTime;
      let phaseStart = autoPhaseStartTime;
      let currentPhase = autoCurrentPhase;
      let startX = arcStartX;
      let endX = arcEndX;

      const animate = () => {
        time += 1;

        // 自动模式逻辑
        if (autoMode) {
          // 使用 verticalSpeed 调整周期时长
          const phaseDuration = (autoCycleDuration * 60) / (verticalSpeed / 20);
          const elapsedInPhase = time - phaseStart;

          if (elapsedInPhase >= phaseDuration) {
            if (currentPhase === 'forward') {
              currentPhase = 'backward';
              phaseStart = time;
              // 随机选择左边(-1)或右边(1)作为起点和终点
              startX = secureRandom() > 0.5 ? 1 : -1;
              endX = secureRandom() > 0.5 ? 1 : -1;
              setArcStartX(startX);
              setArcEndX(endX);
              setAutoCurrentPhase(currentPhase);
              setAutoPhaseStartTime(phaseStart);
            } else {
              currentPhase = 'forward';
              phaseStart = time;
              // 生成新车位号并更新状态
              const newNumber = generateRandomParkingNumber();
              setNumber(newNumber);
              // 随机旋转初始相位（-30到30度之间）
              setRotatePhaseOffset((secureRandom() - 0.5) * 60 * Math.PI / 180);
              // 10%概率360度旋转，90%概率±90度范围内
              setRotateMode(secureRandom() < 0.1 ? 'full' : 'limited');
              // 随机旋转方向
              setRotateDirection(secureRandom() > 0.5 ? 1 : -1);
              // 随机选择左边(-1)或右边(1)作为起点和终点
              startX = secureRandom() > 0.5 ? 1 : -1;
              endX = secureRandom() > 0.5 ? 1 : -1;
              setArcStartX(startX);
              setArcEndX(endX);
              setAutoCurrentPhase(currentPhase);
              setAutoPhaseStartTime(phaseStart);
            }
          }

          const progress = elapsedInPhase / phaseDuration;

          // 纵向运动：文字从底部移动到顶部
          // 全屏时使用屏幕高度，非全屏时使用画布高度
          const baseHeight = isFullscreen ? window.innerHeight : canvasHeight;
          const baseWidth = isFullscreen ? window.innerWidth : canvasWidth * viewWidthMultiplier;

          // 计算缩放后的字体大小（基于对角线，确保旋转时不超出）
          // 估算文字宽度：约为 fontSize * 字符数 * 0.6
          const charCount = number.length;
          const estimatedTextWidth = fontSize * charCount * 0.6;
          const diagonal = Math.sqrt(estimatedTextWidth * estimatedTextWidth + fontSize * fontSize);
          const maxWidth = baseWidth * 0.9;
          const scale = Math.min(1, maxWidth / diagonal);
          const actualFontSize = isFullscreen ? fontSize * scale : fontSize;

          // 留出小边距，让文字能到达屏幕顶部和底部
          const margin = actualFontSize * 0.3;
          const moveDistanceY = baseHeight - margin * 2;
          const moveDistanceX = baseWidth - actualFontSize * charCount * 0.3; // 横向可移动范围

          // 弧线运动：从角落到角落
          // startX/endX 决定起点和终点是左边还是右边
          // forward: 从下往上，backward: 从上往下
          let offsetY, offsetX;

          if (currentPhase === 'forward') {
            // 从底部移动到顶部
            offsetY = (moveDistanceY / 2) - (progress * moveDistanceY);
            // 横向：从startX弧线移动到endX
            // 使用正弦曲线产生弧线效果
            const xStart = startX > 0 ? moveDistanceX / 2 * 0.8 : -moveDistanceX / 2 * 0.8;
            const xEnd = endX > 0 ? moveDistanceX / 2 * 0.8 : -moveDistanceX / 2 * 0.8;
            // 弧线：在中间位置向外弯曲
            const arcDirection = startX > 0 ? -1 : 1; // 从右边开始向左弯，从左边开始向右弯
            const arcOffset = Math.sin(progress * Math.PI) * moveDistanceX * 0.2 * arcDirection;
            offsetX = xStart + (xEnd - xStart) * progress + arcOffset;
          } else {
            // 从顶部移动回底部
            offsetY = -(moveDistanceY / 2) + (progress * moveDistanceY);
            // 横向：从endX弧线移动回新的位置
            const xStart = endX > 0 ? moveDistanceX / 2 * 0.8 : -moveDistanceX / 2 * 0.8;
            const xEnd = startX > 0 ? moveDistanceX / 2 * 0.8 : -moveDistanceX / 2 * 0.8;
            const arcDirection = endX > 0 ? -1 : 1;
            const arcOffset = Math.sin(progress * Math.PI) * moveDistanceX * 0.2 * arcDirection;
            offsetX = xStart + (xEnd - xStart) * progress + arcOffset;
          }

          // 旋转逻辑：90%在±90度范围内摆动，10%做360度旋转
          // time是帧数(60fps)，所以 time/60 是秒数
          let rotation;
          if (rotateMode === 'full') {
            // 360度连续旋转
            rotation = (time / 60) * rotateSpeed * 360 * rotateDirection;
          } else {
            // ±90度范围内摆动，使用正弦函数
            // rotateSpeed控制摆动频率，rotateDirection控制起始方向
            rotation = Math.sin((time / 60) * rotateSpeed * Math.PI * 2) * 90 * rotateDirection;
          }

          setAnimationTime(time);
          setAnimationOffset({
            x: offsetX,
            y: offsetY,
            rotate: rotation
          });
        } else {
          if (autoChangeNumber && time - lastChange > changeInterval * 60) {
            setNumber(generateRandomParkingNumber());
            lastChange = time;
            setLastChangeTime(lastChange);
          }

          // 使用线性移动，从底部到顶部
          const speed = verticalSpeed * 0.1;

          // 全屏时使用屏幕高度，非全屏时使用画布高度
          const baseHeight = isFullscreen ? window.innerHeight : canvasHeight;
          const baseWidth = isFullscreen ? window.innerWidth : canvasWidth * viewWidthMultiplier;

          // 计算缩放后的字体大小
          const charCount = number.length;
          const estimatedTextWidth = fontSize * charCount * 0.6;
          const diagonal = Math.sqrt(estimatedTextWidth * estimatedTextWidth + fontSize * fontSize);
          const maxWidth = baseWidth * 0.9;
          const scale = Math.min(1, maxWidth / diagonal);
          const actualFontSize = isFullscreen ? fontSize * scale : fontSize;

          // 留出小边距，让文字能到达屏幕顶部和底部
          const margin = actualFontSize * 0.3;
          const moveDistanceY = baseHeight - margin * 2;
          const moveDistanceX = baseWidth - actualFontSize * charCount * 0.3;

          // 计算当前周期和进度
          const cycleTime = moveDistanceY / speed; // 一个周期的时间
          const totalProgress = (time * speed) / moveDistanceY;
          const cycleNumber = Math.floor(totalProgress);
          const progress = totalProgress - cycleNumber;
          const isGoingUp = cycleNumber % 2 === 0;

          // 纵向运动
          let offsetY;
          if (isGoingUp) {
            offsetY = (moveDistanceY / 2) - (progress * moveDistanceY);
          } else {
            offsetY = -(moveDistanceY / 2) + (progress * moveDistanceY);
          }

          // 横向弧线运动
          const xStart = (cycleNumber % 4 < 2) ? -moveDistanceX / 2 * 0.8 : moveDistanceX / 2 * 0.8;
          const xEnd = (cycleNumber % 4 < 2) ? moveDistanceX / 2 * 0.8 : -moveDistanceX / 2 * 0.8;
          const arcDirection = xStart < 0 ? 1 : -1;
          const arcOffset = Math.sin(progress * Math.PI) * moveDistanceX * 0.2 * arcDirection;
          const offsetX = xStart + (xEnd - xStart) * progress + arcOffset;

          // 旋转逻辑：90%在±90度范围内摆动，10%做360度旋转
          // time是帧数(60fps)，所以 time/60 是秒数
          let rotation;
          if (rotateMode === 'full') {
            // 360度连续旋转
            rotation = (time / 60) * rotateSpeed * 360 * rotateDirection + (rotatePhaseOffset * 180 / Math.PI);
          } else {
            // ±90度范围内摆动
            rotation = Math.sin((time / 60) * rotateSpeed * Math.PI * 2) * 90 * rotateDirection + (rotatePhaseOffset * 180 / Math.PI);
          }

          setAnimationTime(time);
          setAnimationOffset({
            x: offsetX,
            y: offsetY,
            rotate: rotation
          });
        }

        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, motionDirection, verticalSpeed, horizontalSpeed, rotateSpeed, rotateMode, rotateDirection, autoChangeNumber, changeInterval, autoMode, autoCycleDuration, autoRandomOffset, arcAmplitude, animationTime, lastChangeTime, autoPhaseStartTime, autoCurrentPhase, arcStartX, arcEndX, canvasWidth, canvasHeight, isFullscreen, actualDisplayHeight, viewWidthMultiplier, fontSize, number]);

  // 下载图片
  const handleDownload = () => {
    if (!canvasRef.current || !croppedImage) return;

    drawCanvas(canvasRef.current);

    canvasRef.current.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `parking_${number}_${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  // 重置动画
  const resetAnimation = () => {
    setIsPlaying(false);
    setAnimationOffset({ x: 0, y: 0, rotate: 0 });
    setAnimationTime(0);
    setAutoPhaseStartTime(0);
    setAutoCurrentPhase('forward');
    setArcStartX(0);
    setArcEndX(0);
  };

  // 切换全屏
  const toggleFullscreen = () => {
    if (!previewContainerRef.current) return;

    if (!document.fullscreenElement) {
      previewContainerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
        // 立即更新实际显示高度为屏幕高度
        setActualDisplayHeight(window.innerHeight);
      }).catch(err => {
        console.error('无法进入全屏:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        // 退出全屏后恢复为预览画布高度
        if (previewCanvasRef.current) {
          const rect = previewCanvasRef.current.getBoundingClientRect();
          setActualDisplayHeight(rect.height);
        }
      });
    }
  };

  // 监听全屏变化和显示高度变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);

      // 更新实际显示高度
      if (isNowFullscreen && previewContainerRef.current) {
        setActualDisplayHeight(window.innerHeight);
      } else if (previewCanvasRef.current) {
        // 非全屏时使用画布的显示高度
        const rect = previewCanvasRef.current.getBoundingClientRect();
        setActualDisplayHeight(rect.height);
      }
    };

    const handleResize = () => {
      if (document.fullscreenElement && previewContainerRef.current) {
        setActualDisplayHeight(window.innerHeight);
      } else if (previewCanvasRef.current) {
        const rect = previewCanvasRef.current.getBoundingClientRect();
        setActualDisplayHeight(rect.height);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 页面加载时初始化
  useEffect(() => {
    // 生成第一个随机车位号
    const firstNumber = generateRandomParkingNumber();
    setNumber(firstNumber);

    // 初始化横向随机位置（左边-1或右边1）
    setArcStartX(secureRandom() > 0.5 ? 1 : -1);
    setArcEndX(secureRandom() > 0.5 ? 1 : -1);
    // 随机旋转初始相位（-30到30度之间）
    setRotatePhaseOffset((secureRandom() - 0.5) * 60 * Math.PI / 180);
    // 10%概率360度旋转，90%概率±90度范围内
    setRotateMode(secureRandom() < 0.1 ? 'full' : 'limited');
    // 随机旋转方向
    setRotateDirection(secureRandom() > 0.5 ? 1 : -1);

    console.log('页面加载完成，直接开始播放');
    console.log(`默认画布尺寸: ${canvasWidth}x${canvasHeight}`);
  }, []); // 只在组件挂载时执行一次

  // 自动模式切换时的初始化
  useEffect(() => {
    if (autoMode) {
      setNumber(generateRandomParkingNumber());
      const centerX = 0;
      const range = Math.min(canvasWidth * 0.4, autoRandomOffset * 2);
      const startX = centerX + (secureRandom() - 0.5) * range;
      const endX = centerX + (secureRandom() - 0.5) * range;
      setArcStartX(startX);
      setArcEndX(endX);
      setAutoCurrentPhase('forward');
      setAutoPhaseStartTime(0);
      setAnimationTime(0);
    }
  }, [autoMode]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-center bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
          车位编号屏幕播放器 - 无缝循环版
        </h1>

        {/* 字体加载状态提示 */}
        {!fontsLoaded && (
          <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500/50 rounded-lg text-center">
            <span className="animate-pulse">⏳ 正在加载字体...</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧控制面板 */}
          <div className="lg:col-span-1 space-y-4">
            {/* 图片上传 */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Upload size={20} />
                背景图片
              </h2>
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-yellow-500 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="text-sm text-gray-400">点击上传背景图片</p>
                  <p className="text-xs text-gray-500 mt-1">直接使用原图，不做裁剪</p>
                </div>
                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
              </label>
            </div>

            {/* 编号设置 */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
              <h2 className="text-xl font-semibold mb-4">编号设置</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">编号内容</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      className="flex-1 bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="输入车位编号"
                    />
                    <button
                      onClick={() => setNumber(generateRandomParkingNumber())}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 rounded font-semibold transition-all whitespace-nowrap"
                      title="随机生成车位编号"
                    >
                      🎲 随机
                    </button>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-900/50 to-orange-900/50 border border-yellow-500/30 rounded-lg p-4 mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-semibold text-yellow-200">🎯 临时模式（A171/A-101）</label>
                      <p className="text-xs text-yellow-400 mt-1">
                        开启：只生成易混淆数字车位 | 关闭：生成所有类型车位
                      </p>
                    </div>
                    <button
                      onClick={() => setUseSimplifiedFormat(!useSimplifiedFormat)}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${useSimplifiedFormat ? 'bg-yellow-500' : 'bg-gray-600'
                        }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${useSimplifiedFormat ? 'translate-x-8' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border border-indigo-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-indigo-200">🔄 自动切换编号</label>
                    <button
                      onClick={() => setAutoChangeNumber(!autoChangeNumber)}
                      disabled={autoMode}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoMode ? 'bg-gray-600 opacity-50 cursor-not-allowed' :
                          autoChangeNumber ? 'bg-green-500' : 'bg-gray-600'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoChangeNumber ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                  {autoMode && (
                    <p className="text-xs text-yellow-400 mb-2">
                      ⚠️ 自动模式已接管编号生成
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-2">字体</label>
                  <select
                    value={fontFamily}
                    onChange={(e) => setFontFamily(e.target.value)}
                    className="w-full bg-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    {fonts.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2">字号: {fontSize}px</label>
                  <input
                    type="range"
                    min="50"
                    max="2000"
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2">颜色</label>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-full h-10 bg-gray-700 rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">描边颜色</label>
                    <input
                      type="color"
                      value={strokeColor}
                      onChange={(e) => setStrokeColor(e.target.value)}
                      className="w-full h-10 bg-gray-700 rounded cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-2">亮度: {brightness}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={brightness}
                    onChange={(e) => setBrightness(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">描边宽度: {strokeWidth}px</label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={strokeWidth}
                    onChange={(e) => setStrokeWidth(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">横向位置: {position.x}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={position.x}
                    onChange={(e) => setPosition({ ...position, x: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">纵向位置: {position.y}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={position.y}
                    onChange={(e) => setPosition({ ...position, y: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* 磨损效果 */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
              <h2 className="text-xl font-semibold mb-4">磨损效果</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">磨损强度: {wearIntensity}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={wearIntensity}
                    onChange={(e) => setWearIntensity(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">划痕数量: {scratchCount}</label>
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    value={scratchCount}
                    onChange={(e) => setScratchCount(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* 动画控制 */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
              <h2 className="text-xl font-semibold mb-4">连续播放控制</h2>

              <div className="space-y-4">
                {/* 临时模式开关 */}
                <div className="bg-gradient-to-r from-yellow-900/50 to-orange-900/50 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-bold text-yellow-300 flex items-center gap-2">
                        <span className="text-xl">🎯</span> 临时模式（A171/A-101）
                      </label>
                      <p className="text-xs text-yellow-400 mt-1">
                        开启：只生成易混淆数字车位 | 关闭：生成所有类型车位
                      </p>
                    </div>
                    <button
                      onClick={() => setUseSimplifiedFormat(!useSimplifiedFormat)}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${useSimplifiedFormat ? 'bg-yellow-500' : 'bg-gray-600'
                        }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${useSimplifiedFormat ? 'translate-x-8' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>

                {/* 自动模式 */}
                <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-2 border-green-500/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="text-sm font-bold text-green-300 flex items-center gap-2">
                        <span className="text-xl">🤖</span> 自动模式（推荐）
                      </label>
                      <p className="text-xs text-green-400 mt-1">
                        自动生成车位 → 开进 → 倒出 → 下一个车位
                      </p>
                    </div>
                    <button
                      onClick={() => setAutoMode(!autoMode)}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${autoMode ? 'bg-green-500' : 'bg-gray-600'
                        }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${autoMode ? 'translate-x-8' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {autoMode && (
                    <div className="space-y-3 mt-3 pt-3 border-t border-green-500/30">
                      <div>
                        <label className="block text-xs text-green-300 mb-2">
                          每个动作持续: {autoCycleDuration} 秒
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="60"
                          value={autoCycleDuration}
                          onChange={(e) => setAutoCycleDuration(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-green-300 mb-2">
                          横向随机范围: ±{autoRandomOffset}px
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="300"
                          value={autoRandomOffset}
                          onChange={(e) => setAutoRandomOffset(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-green-300 mb-2">
                          弧线弯曲度: {arcAmplitude}px
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="200"
                          value={arcAmplitude}
                          onChange={(e) => setArcAmplitude(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div className="bg-green-950/50 rounded p-2 mt-2">
                        <p className="text-xs text-green-300">
                          <strong>当前状态:</strong> {autoCurrentPhase === 'forward' ? '🚗→ 开进车位' : '←🚗 倒出车位'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* 手动模式设置 */}
                {!autoMode && (
                  <>
                    <div>
                      <label className="block text-sm mb-3 font-semibold text-yellow-400">运动方向</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setMotionDirection('forward')}
                          className={`px-4 py-3 rounded-lg font-semibold transition-all ${motionDirection === 'forward'
                              ? 'bg-gradient-to-r from-green-500 to-emerald-600 ring-2 ring-green-400'
                              : 'bg-gray-700 hover:bg-gray-600'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-2xl mb-1">🚗→</div>
                            <div className="text-sm">开进车位</div>
                          </div>
                        </button>
                        <button
                          onClick={() => setMotionDirection('backward')}
                          className={`px-4 py-3 rounded-lg font-semibold transition-all ${motionDirection === 'backward'
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-600 ring-2 ring-blue-400'
                              : 'bg-gray-700 hover:bg-gray-600'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-2xl mb-1">←🚗</div>
                            <div className="text-sm">倒出车位</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm mb-2">上下移动速度: {verticalSpeed}</label>
                  <input
                    type="range"
                    min="1"
                    max="1000"
                    value={verticalSpeed}
                    onChange={(e) => setVerticalSpeed(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">左右摆动幅度: {horizontalSpeed.toFixed(1)}</label>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={horizontalSpeed}
                    onChange={(e) => setHorizontalSpeed(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">
                    旋转速度: {rotateSpeed.toFixed(2)} 转/秒
                    {rotateSpeed > 0 && ` (${(1 / rotateSpeed).toFixed(1)}秒/转)`}
                  </label>
                  <input
                    type="range"
                    min="0.01"
                    max="6"
                    step="0.01"
                    value={rotateSpeed}
                    onChange={(e) => setRotateSpeed(parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0.01 (100秒/转)</span>
                    <span>6 (最快)</span>
                  </div>
                </div>

                <div className="border-t border-gray-600 pt-4 mt-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsPlaying(!isPlaying)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all ${isPlaying
                          ? 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
                          : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                        }`}
                    >
                      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                      {isPlaying ? '暂停播放' : '开始播放'}
                    </button>

                    <button
                      onClick={resetAnimation}
                      className="px-4 py-3 rounded-lg font-semibold transition-all bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                      title="重置动画"
                    >
                      <RotateCw size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧预览区域 */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">预览 / 播放窗口</h2>
                <button
                  onClick={toggleFullscreen}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 rounded-lg font-semibold transition-all"
                  title={isFullscreen ? "退出全屏" : "全屏播放"}
                >
                  {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                  {isFullscreen ? '退出全屏' : '全屏播放'}
                </button>
              </div>
              <div
                ref={previewContainerRef}
                className={`rounded-lg overflow-hidden flex items-center justify-center relative ${isFullscreen ? 'min-h-screen' : 'min-h-[600px]'
                  }`}
                style={{ backgroundColor: '#000000' }}
              >
                <canvas
                  ref={isFullscreen ? fullscreenCanvasRef : previewCanvasRef}
                  style={isFullscreen ? {
                    width: '100vw',
                    height: '100vh',
                    objectFit: 'contain',
                    display: 'block'
                  } : {
                    width: '100%',
                    height: 'auto',
                    maxHeight: '700px',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 隐藏的画布用于导出 */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
